import { Alert, Badge, Button } from '@personachat/ui';
import { Check, Copy, RotateCw, Search, X } from 'lucide-react';
import { useEffect, useState, type MouseEvent, type ReactNode, type SelectHTMLAttributes } from 'react';

import { messageDe } from '../api/call';
import { formatNombre } from '../lib/format';
import { LIBELLE_TYPE, statutCompte, TON_TYPE, type TypeCompte } from '../lib/labels';
import { useDebounced } from '../lib/useDebounced';

/** Pastille de type de compte ; « Admin » prime sur le type d'élève. */
export function TypeBadge({ type, isAdmin }: { type: TypeCompte; isAdmin?: boolean }) {
  const cle = isAdmin ? 'ADMIN' : type;
  return <Badge tone={TON_TYPE[cle]}>{LIBELLE_TYPE[cle]}</Badge>;
}

export function StatutBadge({ isActive, deletedAt }: { isActive: boolean; deletedAt?: string | null }) {
  const s = statutCompte({ isActive, deletedAt: deletedAt ?? null });
  return <Badge tone={s.ton}>{s.libelle}</Badge>;
}

/** Jauge d'utilisation « 12 / 20 ». */
export function Jauge({ utilise, max, indicatif = false }: { utilise: number; max: number; indicatif?: boolean }) {
  const ratio = max > 0 ? Math.min(1, utilise / max) : 0;
  const ton = utilise >= max ? 'plein' : ratio >= 0.9 ? 'haut' : 'normal';
  return (
    <span className="jauge" title={indicatif ? 'Limite indicative : l’application ne bloque pas au-delà.' : undefined}>
      <span className="jauge__barre" aria-hidden="true">
        <span className={`jauge__rempli jauge__rempli--${ton}`} style={{ width: `${Math.round(ratio * 100)}%` }} />
      </span>
      <span className="jauge__texte">
        {formatNombre(utilise)} / {formatNombre(max)}
      </span>
    </span>
  );
}

/** Liste déroulante native aux couleurs de la charte. */
export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={['ui-input', 'select', className].filter(Boolean).join(' ')} {...rest} />;
}

/** Filtre libellé (barre de filtres). */
export function Filtre({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="filtre">
      <label className="filtre__label" htmlFor={id}>
        {label}
      </label>
      {children}
    </div>
  );
}

/** Champ de recherche : la valeur remonte après 300 ms sans frappe. */
export function Recherche({
  id,
  valeur,
  onChange,
  placeholder,
  label,
}: {
  id: string;
  valeur: string;
  onChange: (v: string) => void;
  placeholder: string;
  label: string;
}) {
  const [texte, setTexte] = useState(valeur);
  const retarde = useDebounced(texte, 300);
  useEffect(() => setTexte(valeur), [valeur]);
  // Seule la saisie retardée déclenche la remontée (pas un changement de `valeur` venu de l'URL).
  useEffect(() => {
    if (retarde !== valeur) onChange(retarde);
  }, [retarde]);
  return (
    <div className="recherche">
      <Search size={15} aria-hidden="true" className="recherche__icone" />
      <input
        id={id}
        className="ui-input recherche__input"
        value={texte}
        onChange={(e) => setTexte(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        type="search"
      />
      {texte && (
        <button type="button" className="recherche__effacer" onClick={() => setTexte('')} aria-label="Effacer la recherche">
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/** Erreur de chargement : le message de l'API et « Réessayer » (cahier des charges §4). */
export function ErreurChargement({ erreur, onReessayer }: { erreur: unknown; onReessayer: () => void }) {
  return (
    <div className="stack">
      <Alert>{messageDe(erreur)}</Alert>
      <div>
        <Button size="sm" icon={<RotateCw size={14} aria-hidden="true" />} onClick={onReessayer}>
          Réessayer
        </Button>
      </div>
    </div>
  );
}

/** Liste « libellé : valeur » des fiches. */
export function InfoList({ items }: { items: Array<{ label: string; value: ReactNode; hidden?: boolean }> }) {
  return (
    <dl className="infolist">
      {items
        .filter((i) => !i.hidden)
        .map((i) => (
          <div key={i.label} className="infolist__row">
            <dt>{i.label}</dt>
            <dd>{i.value}</dd>
          </div>
        ))}
    </dl>
  );
}

/** Code (clé, identifiant) en police mono, copiable en un clic. */
export function Code({ valeur, copiable = true }: { valeur: string; copiable?: boolean }) {
  const [copie, setCopie] = useState(false);
  const copier = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(valeur);
      setCopie(true);
      window.setTimeout(() => setCopie(false), 1500);
    } catch {
      // Presse-papiers refusé (contexte non sécurisé) : le code reste sélectionnable.
    }
  };
  return (
    <span className="code">
      <code>{valeur}</code>
      {copiable && (
        <button type="button" className="code__copy" onClick={copier} aria-label={copie ? 'Copié' : `Copier ${valeur}`}>
          {copie ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
        </button>
      )}
    </span>
  );
}

/** Section titrée d'une fiche ou d'une page. */
export function Section({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="section">
      <div className="section__header">
        <h3 className="section__title">{title}</h3>
        {actions && <div className="section__actions">{actions}</div>}
      </div>
      {children}
    </section>
  );
}
